import React, { useMemo } from 'react';
import { DateTime, Duration } from 'luxon';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Typography, theme } from 'antd';
import { RotationsWidgetCollapsible } from './rotations-widget-collapsible.component';
import { isRotationsEnabled as isRotationsEnabledSelector } from 'shared/store/selectors/features.selector';
import { colorPalette } from 'shared/constants';
import { useTimeslots, useRotationFromUser } from 'shared/hooks/rotations.hooks';
import _ from 'lodash';

const RANGE = Duration.fromObject({ month: 12 });

export function RotationsWidget() {
  const { token } = theme.useToken();
  const lang = useSelector(getLang('DASHBOARD'));
  const isRotationsEnabled = useSelector(isRotationsEnabledSelector);
  const user = useSelector(state => state.auth.user);

  const range = useMemo(() => {
    const today = DateTime.now().startOf('day');
    return { start: today.toJSDate(), end: today.plus(RANGE).toJSDate() };
  }, []);

  const timeslotsQuery = useTimeslots(
    { userIds: [user.id], ...range },
    { enabled: isRotationsEnabled && !!user.id },
  );

  const profileRotation = user.professional?.rotation;
  const shouldUseFallback =
    isRotationsEnabled &&
    timeslotsQuery.isSuccess &&
    (!timeslotsQuery.data || timeslotsQuery.data.length === 0) &&
    !!profileRotation;

  const rotationFromUserQuery = useRotationFromUser({ enabled: shouldUseFallback });

  if (!isRotationsEnabled) return null;

  if (timeslotsQuery.isPending) return null;

  if (timeslotsQuery.isError) return null;

  const data = (() => {
    if (timeslotsQuery.data?.length) return timeslotsQuery.data;
    if (
      shouldUseFallback &&
      rotationFromUserQuery.isSuccess &&
      !_.isEmpty(rotationFromUserQuery.data)
    ) {
      return [{ rotation: rotationFromUserQuery.data, start: Date.now() }];
    }
    return null;
  })();

  if (rotationFromUserQuery.isError) return null;

  if (!data || data.length === 0) return null;

  return (
    <div style={{ marginTop: token.marginXL }}>
      <Typography.Title
        level={3}
        style={{ marginBottom: token.marginLG, color: colorPalette.primaryMain8 }}>
        {lang.TITLE_ROTATIONS}
      </Typography.Title>
      <RotationsWidgetCollapsible rotationTimeslots={data} />
    </div>
  );
}
